<script setup>
import { computed, onMounted, ref } from 'vue';
import '../forms/tmsTheme.css';
import { clinicProcess } from './clinicAxios';
import TmsTable from '../table/TmsTable.vue';
import TSeparator from '../forms/TSeparator.vue';
import { requestStatusClass } from '../requests/requestStatus';
import CustomItems from '../requests/CustomItems.vue';
import RequestDetail from '../requests/RequestDetail.vue';
import { formatRequestRef } from '../requests/requestRef';
import { Notify } from '../forms/tmsUi';

const PAGE_SIZE = 10;
const token = ref(localStorage.getItem('clinicToken') || '');
const username = ref('');
const password = ref('');
const nextPassword = ref('');
const notice = ref('');
const me = ref(null);
const view = ref('list');
const requests = ref([]);
const types = ref([]);
const statusFilter = ref('');
const page = ref(1);
const search = ref('');
const selected = ref(null);
const viewOpen = ref(false);
const saving = ref(false);
const cancelling = ref(false);
const form = ref(blankForm());

const tabs = [
  { label: 'Requests', view: 'list' },
  { label: 'My clinic', view: 'clinic' },
];

const titles = { list: 'Requests', new: 'New request', clinic: 'My clinic' };

const mustChange = computed(() => !!(me.value && me.value.mustChangePassword));
const clinicName = computed(() => (me.value && me.value.clinic && me.value.clinic.name) || 'Clinic');
const pageTitle = computed(() => titles[view.value] || '');

const columns = [
  { name: 'refLabel', label: 'Ref', field: 'refLabel', align: 'left', bold: true },
  { name: 'displayStatus', label: 'Status', field: 'displayStatus', align: 'left', type: 'chip', chipClass: statusClass },
  { name: 'typeLabel', label: 'Type', field: 'typeLabel', align: 'left' },
  { name: 'urgentLabel', label: 'Urgent', field: 'urgentLabel', align: 'left', type: 'chip', chipClass: () => 'tms-status-cancelled' },
  { name: 'returnLabel', label: 'Return', field: 'returnLabel', align: 'left', type: 'chip', chipClass: () => 'tms-status-assigned' },
  { name: 'readyBy', label: 'Ready by', field: 'readyBy', align: 'left' },
  { name: 'createdAt', label: 'Created', field: 'createdAt', align: 'left' },
];

function blankForm() {
  return { requestType: 'pickup', urgent: false, returnRequired: false, readyBy: '', note: '', items: [], customItems: [] };
}

function statusClass(row) {
  return requestStatusClass(row.displayStatus);
}

function tabActive(target) {
  if (target === 'list') return view.value === 'list' || view.value === 'new';
  return view.value === target;
}

const filtered = computed(() => {
  const needle = search.value.trim().toLowerCase();
  if (!needle) return requests.value;
  return requests.value.filter((row) =>
    [row.refLabel, row.displayStatus, row.typeLabel, row.urgentLabel, row.readyBy, row.createdAt, row.note]
      .join(' ')
      .toLowerCase()
      .includes(needle)
  );
});

const total = computed(() => filtered.value.length);
const pageRows = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE;
  return filtered.value.slice(start, start + PAGE_SIZE);
});

const visibleItems = computed(() =>
  form.value.items.filter((item) =>
    form.value.requestType === 'delivery' ? item.direction === 'deliver' : item.direction !== 'deliver'
  )
);

function setQuantity(item, value) {
  const qty = Math.floor(Number(value));
  item.quantity = Number.isFinite(qty) && qty > 0 ? qty : 0;
}

async function login() {
  notice.value = '';
  try {
    const data = await clinicProcess('clinic-login', { username: username.value, password: password.value });
    localStorage.setItem('clinicToken', data.token);
    token.value = data.token;
    password.value = '';
    await loadMe();
  } catch (error) {
    notice.value = error.message;
  }
}

function logout() {
  if (token.value) clinicProcess('clinic-logout', {}).catch(() => {});
  localStorage.removeItem('clinicToken');
  token.value = '';
  me.value = null;
  viewOpen.value = false;
  view.value = 'list';
}

async function loadMe() {
  try {
    me.value = await clinicProcess('clinic-me', {});
    if (!me.value.mustChangePassword) {
      await Promise.all([loadRequests(), loadTypes()]);
    }
  } catch (error) {
    if (error.type === 'password') {
      me.value = { mustChangePassword: true, clinic: {} };
      return;
    }
    logout();
    notice.value = error.message;
  }
}

async function changePassword() {
  notice.value = '';
  try {
    await clinicProcess('clinic-change-password', { password: nextPassword.value });
    nextPassword.value = '';
    await loadMe();
  } catch (error) {
    notice.value = error.message;
  }
}

function resetForm() {
  form.value = {
    ...blankForm(),
    items: types.value.map((type) => ({
      itemTypeId: type.id,
      name: type.name,
      direction: type.direction,
      quantity: 0,
    })),
  };
}

async function loadTypes() {
  const data = await clinicProcess('clinic-item-types', {});
  types.value = data.types || [];
  resetForm();
}

async function loadRequests() {
  const data = await clinicProcess('clinic-list-requests', {
    displayStatus: statusFilter.value || undefined,
  });
  requests.value = (data.requests || []).map((row) => ({
    ...row,
    typeLabel: row.requestType === 'delivery' ? 'Delivery' : 'Pickup',
    urgentLabel: row.urgent ? 'Urgent' : '-',
    returnLabel: row.returnRequired ? 'Return' : '-',
    refLabel: formatRequestRef(row),
    readyBy: row.readyBy || '-',
  }));
}

function onSearch(value) {
  search.value = value;
  page.value = 1;
}

async function setStatus(value) {
  statusFilter.value = value;
  page.value = 1;
  try {
    await loadRequests();
  } catch (error) {
    notice.value = error.message;
  }
}

function openNew() {
  notice.value = '';
  resetForm();
  view.value = 'new';
}

function closeNew() {
  notice.value = '';
  view.value = 'list';
}

async function openRequest(row) {
  notice.value = '';
  try {
    selected.value = await clinicProcess('clinic-get-request', { id: row.id });
    viewOpen.value = true;
  } catch (error) {
    notice.value = error.message;
  }
}

async function createRequest() {
  if (saving.value) return;
  notice.value = '';
  saving.value = true;
  try {
    const custom = form.value.customItems.filter((item) => String(item.name || '').trim());
    if (custom.some((item) => !(Number(item.quantity) > 0))) {
      notice.value = 'Give each other item a quantity of at least 1, or remove it.';
      return;
    }
    const items = visibleItems.value
      .filter((item) => Number(item.quantity) > 0)
      .map((item) => ({ itemTypeId: item.itemTypeId, quantity: Number(item.quantity) }))
      .concat(custom.map((item) => ({ customName: item.name.trim(), quantity: Number(item.quantity) })));
    const created = await clinicProcess('clinic-create-request', {
      requestType: form.value.requestType,
      urgent: form.value.urgent,
      returnRequired: form.value.requestType === 'pickup' && form.value.returnRequired,
      readyBy: form.value.readyBy ? new Date(form.value.readyBy).getTime() : null,
      note: form.value.note,
      items,
    });
    resetForm();
    view.value = 'list';
    Notify.create({
      message: created && created.referenceNumber ? `Request ${created.referenceNumber} sent` : 'Request sent',
      classes: 'bg-green px-4',
    });
    await loadRequests();
  } catch (error) {
    notice.value = error.message;
  } finally {
    saving.value = false;
  }
}

async function cancelRequest() {
  if (!selected.value || cancelling.value) return;
  cancelling.value = true;
  try {
    await clinicProcess('clinic-cancel-request', { id: selected.value.id });
    viewOpen.value = false;
    await loadRequests();
  } catch (error) {
    notice.value = error.message;
  } finally {
    cancelling.value = false;
  }
}

onMounted(() => {
  if (token.value) loadMe();
});
</script>

<template>
  <div class="clinic-portal">
    <div class="clinic-portal-inner tms-main">
      <vx-card v-if="!token" title="Clinic sign in" class="clinic-portal-auth">
        <p v-if="notice" class="tms-alert">{{ notice }}</p>
        <form class="flex flex-col gap-3" @submit.prevent="login">
          <div class="tms-field">
            <label class="tms-label">Username</label>
            <vs-input class="w-full" v-model="username" />
          </div>
          <div class="tms-field">
            <label class="tms-label">Password</label>
            <vs-input class="w-full" type="password" v-model="password" />
          </div>
          <vs-button class="w-full" button="submit">Sign in</vs-button>
        </form>
      </vx-card>

      <vx-card v-else-if="mustChange" title="Choose a new password" class="clinic-portal-auth">
        <p v-if="notice" class="tms-alert">{{ notice }}</p>
        <p class="mb-3">Choose a new password of at least 8 characters before continuing.</p>
        <form class="flex flex-col gap-3" @submit.prevent="changePassword">
          <div class="tms-field">
            <label class="tms-label">New password</label>
            <vs-input class="w-full" type="password" v-model="nextPassword" />
          </div>
          <vs-button class="w-full" button="submit">Save password</vs-button>
          <vs-button class="w-full" type="border" @click="logout">Sign out</vs-button>
        </form>
      </vx-card>

      <template v-else>
        <div class="clinic-portal-header">
          <div class="font-semibold text-lg">{{ clinicName }}</div>
          <vs-button size="small" type="border" icon-pack="feather" icon="icon-log-out" @click="logout">Sign out</vs-button>
        </div>

        <div class="flex flex-wrap gap-2 mb-4">
          <vs-button
            v-for="tab in tabs"
            :key="tab.view"
            :type="tabActive(tab.view) ? 'filled' : 'border'"
            @click="view = tab.view; notice = ''"
          >
            {{ tab.label }}
          </vs-button>
        </div>

        <vx-card :title="pageTitle" :class="{ 'tms-form-card': view === 'new' }">
          <p v-if="notice" class="tms-alert">{{ notice }}</p>

          <div v-if="view === 'clinic'" class="tms-driver-view">
            <div class="font-semibold text-lg">{{ clinicName }}</div>
            <div><strong>Address:</strong> {{ (me && me.clinic.address) || '-' }}</div>
            <div><strong>PIC name:</strong> {{ (me && me.clinic.picName) || '-' }}</div>
            <div><strong>PIC phone:</strong> {{ (me && me.clinic.picPhone) || '-' }}</div>
          </div>

          <div v-else-if="view === 'new'">
            <div class="flex flex-wrap -mx-2">
              <div class="w-full lg:w-1/2 flex flex-col gap-3 px-2">
                <TSeparator label="Details" />
                <div class="tms-field">
                  <label class="tms-label">Type</label>
                  <vs-select class="w-full" :value="form.requestType" @input="form.requestType = $event">
                    <vs-select-item value="pickup" text="Pickup" />
                    <vs-select-item value="delivery" text="Delivery" />
                  </vs-select>
                </div>
                <div class="tms-field">
                  <label class="tms-label">Ready by</label>
                  <vs-input class="w-full" type="datetime-local" v-model="form.readyBy" />
                </div>
                <vs-checkbox v-model="form.urgent">Urgent</vs-checkbox>
                <vs-checkbox v-if="form.requestType === 'pickup'" v-model="form.returnRequired">
                  Return of samples required 
                </vs-checkbox>
                <div class="tms-field">
                  <label class="tms-label">Note</label>
                  <vs-textarea class="w-full" v-model="form.note" />
                </div>
              </div>
              <div class="w-full lg:w-1/2 flex flex-col gap-3 px-2">
                <TSeparator label="Items" />
                <div v-for="item in visibleItems" :key="item.itemTypeId" class="clinic-portal-item">
                  <label class="tms-label">{{ item.name }}</label>
                  <vs-input
                    class="clinic-portal-qty"
                    type="number"
                    min="0"
                    step="1"
                    :value="item.quantity"
                    @input="setQuantity(item, $event)"
                  />
                </div>
                <div v-if="!visibleItems.length" class="tms-route-add-note">No item types for this request.</div>
                <TSeparator label="Other items" />
                <p class="tms-route-add-note">Anything not in the list above. The rider and the lab count these too.</p>
                <CustomItems v-model="form.customItems" :disabled="saving" />
              </div>
            </div>
            <div class="tms-actions">
              <vs-button type="border" :disabled="saving" @click="closeNew">Cancel</vs-button>
              <vs-button :disabled="saving" @click="createRequest">{{ saving ? 'Submitting...' : 'Submit' }}</vs-button>
            </div>
          </div>

          <TmsTable
            v-else
            compact
            remote
            :data="pageRows"
            :page="page"
            :total="total"
            :pageSize="PAGE_SIZE"
            :columns="columns"
            :enableViewAction="true"
            :enableEditAction="false"
            row-key="id"
            @view-row="openRequest"
            @search="onSearch"
            @page="page = $event"
          >
            <template slot="actions">
              <vs-button
                radius
                icon-pack="feather"
                icon="icon-plus"
                title="New request"
                @click="openNew"
              />
              <vs-select class="clinic-portal-filter" :value="statusFilter" @input="setStatus">
                <vs-select-item value="" text="All statuses" />
                <vs-select-item value="Requested" text="Requested" />
                <vs-select-item value="Assigned" text="Assigned" />
                <vs-select-item value="Picked up" text="Picked up" />
                <vs-select-item value="Delivered" text="Delivered" />
                <vs-select-item value="Received by lab" text="Received by lab" />
                <vs-select-item value="Completed" text="Completed" />
                <vs-select-item value="Rejected" text="Rejected" />
                <vs-select-item value="Cancelled" text="Cancelled" />
              </vs-select>
            </template>
          </TmsTable>
        </vx-card>
      </template>
    </div>

    <vs-popup :title="selected ? `Request ${formatRequestRef(selected)}` : 'Request'" :active.sync="viewOpen" class="large-popup">
      <div v-if="selected">
        <RequestDetail :request="selected" />
        <div class="tms-actions">
          <vs-button type="border" @click="viewOpen = false">Close</vs-button>
          <vs-button
            v-if="selected.status === 'requested' || selected.status === 'assigned'"
            color="danger"
            type="border"
            :disabled="cancelling"
            @click="cancelRequest"
          >{{ cancelling ? 'Cancelling...' : 'Cancel request' }}</vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<style scoped>
.clinic-portal {
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.clinic-portal-inner {
  max-width: 960px;
  margin: 0 auto;
  overflow: visible;
}

.clinic-portal-auth {
  max-width: 420px;
  margin: 10vh auto 0;
}

.clinic-portal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.clinic-portal-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.clinic-portal-qty {
  width: 110px;
  flex-shrink: 0;
}

.clinic-portal-filter {
  width: 180px;
}
</style>
